import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, MessageSquare, Cpu, AlertTriangle, ShieldCheck, ArrowRight,
  Plus, Loader2, RefreshCw, Activity, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

function HeroTile({ label, value, sub, Icon, tone = 'slate', testid }) {
  const toneMap = {
    emerald: 'bg-emerald-500/10 border-emerald-400/30',
    amber: 'bg-amber-500/10 border-amber-400/30',
    sky: 'bg-sky-500/10 border-sky-400/30',
    slate: 'bg-white/5 border-white/10',
    rose: 'bg-rose-500/10 border-rose-400/30',
  }[tone];
  const iconTone = {
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    sky: 'text-sky-300',
    slate: 'text-white/55',
    rose: 'text-rose-300',
  }[tone];
  return (
    <div className={`rounded-2xl border ${toneMap} p-3 sm:p-4 text-white backdrop-blur-sm`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] sm:text-xs font-medium text-white/65">{label}</span>
        <Icon className={`w-4 h-4 ${iconTone}`} />
      </div>
      <div className="mt-1.5 text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>{value}</div>
      {sub && <div className="text-[10px] sm:text-[11px] text-white/55 mt-0.5">{sub}</div>}
    </div>
  );
}

/**
 * Owner hero: prominent Pilot Access card with KPIs and big action buttons.
 * Fetches pilot count + feedback count + a summary of device profiles awaiting
 * review. No live control. No external calls.
 */
export default function PilotHeroCard({ token, onOpenPilotAccess, onOpenInbox }) {
  const [pilots, setPilots] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [counts, setCounts] = useState({ devices: null, pending: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [pRes, fRes] = await Promise.all([
        axios.get(`${API}/admin/pilot-devices`, { headers }),
        axios.get(`${API}/admin/pilot-devices/feedback`, { headers }),
      ]);
      setPilots(pRes.data?.pilots || pRes.data?.items || []);
      setFeedback(fRes.data?.items || fRes.data?.feedback || []);
    } catch (e) {
      // Non-fatal: hero still renders with placeholders
    }
    try {
      // Lightweight device + pending count from admin device hub (if available)
      const r = await axios.get(`${API}/admin/device-onboarding/profiles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = r.data?.items || [];
      setCounts({
        devices: items.length,
        pending: items.filter((d) => d.pending_review).length,
      });
    } catch {
      setCounts({ devices: 0, pending: 0 });
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const totalPilots = pilots ? pilots.filter((p) => !p.revoked).length : null;
  const activePilots = pilots ? pilots.filter((p) => p.status && p.status !== 'revoked').length : null;
  const newFeedback = feedback ? feedback.length : null;

  return (
    <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-2xl p-5 sm:p-6 shadow-sm"
      data-testid="cc-pilot-hero">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-emerald-100">Pilot Access</p>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mt-0.5">
            Mijn Powersure pilots
          </h2>
          <p className="text-sm text-emerald-50/90 mt-1 max-w-xl">
            Maak pilot-codes aan, deel via WhatsApp en bekijk feedback van uw eerste klanten.
          </p>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] bg-white/15 px-2.5 py-1 rounded-full"
          data-testid="cc-live-control-badge">
          <ShieldCheck className="w-3 h-3" />
          Live control: UIT
        </div>
      </div>

      {/* KPI tiles inline (light on dark) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4"
        data-testid="cc-pilot-hero-tiles">
        <div className="rounded-xl bg-white/12 backdrop-blur-sm px-3 py-2" data-testid="cc-tile-pilots">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-emerald-100">Pilots</span>
            <Users className="w-3.5 h-3.5 text-emerald-100" />
          </div>
          <div className="text-2xl font-semibold mt-0.5">
            {loading ? <span className="opacity-60">&hellip;</span> : (activePilots ?? totalPilots ?? 0)}
          </div>
        </div>
        <div className="rounded-xl bg-white/12 backdrop-blur-sm px-3 py-2" data-testid="cc-tile-feedback">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-emerald-100">Nieuwe feedback</span>
            <MessageSquare className="w-3.5 h-3.5 text-emerald-100" />
          </div>
          <div className="text-2xl font-semibold mt-0.5">
            {loading ? <span className="opacity-60">&hellip;</span> : (newFeedback ?? 0)}
          </div>
        </div>
        <div className="rounded-xl bg-white/12 backdrop-blur-sm px-3 py-2" data-testid="cc-tile-devices">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-emerald-100">Apparaten toegevoegd</span>
            <Cpu className="w-3.5 h-3.5 text-emerald-100" />
          </div>
          <div className="text-2xl font-semibold mt-0.5">
            {loading ? <span className="opacity-60">&hellip;</span> : (counts.devices ?? 0)}
          </div>
        </div>
        <div className="rounded-xl bg-white/12 backdrop-blur-sm px-3 py-2" data-testid="cc-tile-pending">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-emerald-100">Wacht op controle</span>
            <AlertTriangle className="w-3.5 h-3.5 text-emerald-100" />
          </div>
          <div className="text-2xl font-semibold mt-0.5">
            {loading ? <span className="opacity-60">&hellip;</span> : (counts.pending ?? 0)}
          </div>
        </div>
      </div>

      {/* Big actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-4" data-testid="cc-pilot-hero-actions">
        <button onClick={onOpenPilotAccess}
          data-testid="cc-hero-new-pilot"
          className="bg-white text-emerald-700 rounded-xl px-4 py-3 font-semibold flex items-center justify-between hover:bg-emerald-50 transition-colors">
          <span className="flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nieuwe pilot aanmaken
          </span>
          <ArrowRight className="w-5 h-5" />
        </button>
        <button onClick={onOpenPilotAccess}
          data-testid="cc-hero-view-feedback"
          className="bg-emerald-900/40 hover:bg-emerald-900/60 text-white rounded-xl px-4 py-3 font-semibold flex items-center justify-between transition-colors">
          <span className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" /> Bekijk feedback &amp; apparaten
          </span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}

/**
 * Secondary KPI strip — shown right under the hero in the owner-app view.
 */
export function OwnerKpiStrip({ summary }) {
  if (!summary) return null;
  const today = summary?.kpis?.today || {};
  const platform = summary?.platform || {};
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cc-owner-kpis">
      <HeroTile label="Leads vandaag" value={today.quickscan_started ?? 0}
        sub="Quickscans gestart" Icon={Activity} tone="sky" testid="cc-owner-kpi-leads" />
      <HeroTile label="Offertes vandaag" value={today.checkout_started ?? 0}
        sub="Checkouts gestart" Icon={Activity} tone="amber" testid="cc-owner-kpi-quotes" />
      <HeroTile label="Betalingen 24u" value={platform.paid_payments_24h ?? 0}
        sub={`${platform.active_subscribers ?? 0} actieve abonnees`}
        Icon={CheckCircle2} tone="emerald" testid="cc-owner-kpi-payments" />
      <HeroTile label="Safety" value="OK"
        sub="Sturing uit · geen externe calls"
        Icon={ShieldCheck} tone="emerald" testid="cc-owner-kpi-safety" />
    </div>
  );
}
